import { ChangeDetectionStrategy, Component } from '@angular/core';
import { SharedModule } from '@shared/modules/shared.module';

interface ColorVariable {
	name: string;
	variable: string;
	value: string;
	category: string;
}

@Component({
	selector: 'app-ui-theme-styles',
	imports: [SharedModule],
	templateUrl: './ui-theme-styles.component.html',
	styleUrl: './ui-theme-styles.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiThemeStylesComponent {
	shadows = [
		{ name: 'Card', variable: '--shadow-card', value: '0px 40px 40px rgba(35, 35, 35, 0.05)' },
		{ name: 'Input', variable: '--shadow-input', value: '0px 0px 6px rgba(108, 117, 125, 0.2)' },
		{ name: 'Elements', variable: '--shadow-elemnts', value: '0px 0px 40px rgba(0, 0, 0, 0.05)' },
		{ name: 'Dropdown', variable: '--shadow-dropdown', value: '0px 10px 6px 0px rgba(108, 117, 125, 0.15)' },
	];

	colors: ColorVariable[] = [
		{ name: 'White', variable: '--color-white', value: '#ffffff', category: 'Base Colors' },
		{ name: 'Black', variable: '--color-black', value: '#000000', category: 'Base Colors' },

		{ name: 'Primary 100', variable: '--primary-color-100', value: '#0E1F2F', category: 'Primary Palette' },
		{ name: 'Primary 80', variable: '--primary-color-80', value: '#3E4C59', category: 'Primary Palette' },
		{ name: 'Primary 60', variable: '--primary-color-60', value: '#6E7882', category: 'Primary Palette' },
		{ name: 'Primary 40', variable: '--primary-color-40', value: '#9FA5AC', category: 'Primary Palette' },

		{ name: 'Secondary 100', variable: '--secondary-color-100', value: '#34495E', category: 'Secondary Palette' },
		{ name: 'Secondary 80', variable: '--secondary-color-80', value: '#5D6D7E', category: 'Secondary Palette' },
		{ name: 'Secondary 60', variable: '--secondary-color-60', value: '#85929E', category: 'Secondary Palette' },
		{ name: 'Secondary 40', variable: '--secondary-color-40', value: '#AEB6BF', category: 'Secondary Palette' },

		{ name: 'Toast Info Light', variable: '--toast-info-light', value: '#F1F8FD', category: 'Toast Palette' },
		{ name: 'Toast Info Dark', variable: '--toast-info-dark', value: '#4068E0', category: 'Toast Palette' },
		{ name: 'Toast Success Light', variable: '--toast-success-light', value: '#F0FCF2', category: 'Toast Palette' },
		{ name: 'Toast Success Dark', variable: '--toast-success-dark', value: '#58B68E', category: 'Toast Palette' },
		{ name: 'Toast Warning Light', variable: '--toast-warning-light', value: '#FFFBF1', category: 'Toast Palette' },
		{ name: 'Toast Warning Dark', variable: '--toast-warning-dark', value: '#D2894B', category: 'Toast Palette' },
		{ name: 'Toast Error Light', variable: '--toast-error-light', value: '#FDF6F6', category: 'Toast Palette' },
		{ name: 'Toast Error Dark', variable: '--toast-error-dark', value: '#D24548', category: 'Toast Palette' },

		{ name: 'Success Dark', variable: '--status-success-dark', value: '#58B68E', category: 'Status Palette' },
		{ name: 'Success', variable: '--status-success', value: '#B8E4BE', category: 'Status Palette' },
		{ name: 'Success Light', variable: '--status-success-light', value: '#F0FCF2', category: 'Status Palette' },
		{ name: 'Pistachio Green', variable: '--pistachio-green', value: '#A0A603', category: 'Status Palette' },

		{ name: 'Error Dark', variable: '--status-error-dark', value: '#D24548', category: 'Status Palette' },
		{ name: 'Error', variable: '--status-error', value: '#F2C7C8', category: 'Status Palette' },
		{ name: 'Error Light', variable: '--status-error-light', value: '#FDF6F6', category: 'Status Palette' },

		{ name: 'Blue Dark', variable: '--status-blue-dark', value: '#4068E0', category: 'Status Palette' },
		{ name: 'Blue', variable: '--status-blue', value: '#D0DCEE', category: 'Status Palette' },
		{ name: 'Blue Light', variable: '--status-blue-light', value: '#F1F8FD', category: 'Status Palette' },

		{ name: 'Gray 1', variable: '--gray-1', value: '#212529', category: 'Grayscale Palette' },
		{ name: 'Gray 2', variable: '--gray-2', value: '#343A40', category: 'Grayscale Palette' },
		{ name: 'Gray 3', variable: '--gray-3', value: '#495057', category: 'Grayscale Palette' },
		{ name: 'Gray 4', variable: '--gray-4', value: '#6C757D', category: 'Grayscale Palette' },
		{ name: 'Gray 5', variable: '--gray-5', value: '#ADB5BD', category: 'Grayscale Palette' },
		{ name: 'Gray 6', variable: '--gray-6', value: '#CED4DA', category: 'Grayscale Palette' },
		{ name: 'Gray 7', variable: '--gray-7', value: '#DEE2E6', category: 'Grayscale Palette' },
		{ name: 'Gray 8', variable: '--gray-8', value: '#E9ECEF', category: 'Grayscale Palette' },
		{ name: 'Gray 9', variable: '--gray-9', value: '#F8F9FA', category: 'Grayscale Palette' },
		{ name: 'Gray 10', variable: '--gray-10', value: '#FCFCFC', category: 'Grayscale Palette' },

		{ name: 'Gray 1 (Dark)', variable: '--gray-1', value: '#FCFCFC', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 2 (Dark)', variable: '--gray-2', value: '#F8F9FA', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 3 (Dark)', variable: '--gray-3', value: '#E9ECEF', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 4 (Dark)', variable: '--gray-4', value: '#DEE2E6', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 5 (Dark)', variable: '--gray-5', value: '#CED4DA', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 6 (Dark)', variable: '--gray-6', value: '#ADB5BD', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 7 (Dark)', variable: '--gray-7', value: '#6C757D', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 8 (Dark)', variable: '--gray-8', value: '#495057', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 9 (Dark)', variable: '--gray-9', value: '#343A40', category: 'Grayscale Palette Dark' },
		{ name: 'Gray 10 (Dark)', variable: '--gray-10', value: '#212529', category: 'Grayscale Palette Dark' },
	];

	getColorsByCategory(category: string): ColorVariable[] {
		return this.colors.filter((c) => c.category === category);
	}

	categories = [
		'Base Colors',
		'Primary Palette',
		'Secondary Palette',
		'Status Palette',
		'Grayscale Palette',
		'Grayscale Palette Dark',
		'Toast Palette',
	];

	copyToClipboard(variable: string): void {
		navigator.clipboard.writeText(variable).then(
			() => {
				// Text copied successfully
			},
			(err) => {
				console.error('Could not copy text: ', err);
			},
		);
	}
}
